import React, { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard, Ticket, Users, Building2, Megaphone, QrCode, Sliders, CreditCard, TrendingUp, Image, Tag, ShoppingBag, BedDouble, Globe, Upload, X, Pencil, Trash2, Power, UtensilsCrossed, CalendarDays,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { toast } from "sonner";
import { DashboardShell, StatCard } from "../components/DashboardShell";
import { ExperiencesTab, CustomersTab, SettlementTab, ContentTab, AuditTab, ProductsAdminTab } from "./adminTabs";
import { AccommodationsAdminTab } from "./adminAccommodations";
import { RestaurantsAdminTab } from "./adminRestaurants";
import { TerritoriesAdminTab } from "./adminTerritories";
import { ActivitiesAdminTab } from "./adminActivities";
import { MediaTab, CategoriesTab } from "./adminCms";
import { AdminLoading, AdminError, AdminEmpty } from "../components/AdminState";
import { downloadQR, copyLink } from "../lib/qr";
import { eur, fracToPct, pctToFrac, commissionLabel } from "../lib/format";
import * as apiClient from "../api";

const TABS = [
  { key: "overview", label: "Panoramica", icon: LayoutDashboard },
  { key: "experiences", label: "Esperienze", icon: Sliders },
  { key: "activities", label: "Eventi & Servizi", icon: CalendarDays },
  { key: "products", label: "Prodotti", icon: ShoppingBag },
  { key: "accommodations", label: "Alloggi", icon: BedDouble },
  { key: "restaurants", label: "Ristoranti", icon: UtensilsCrossed },
  { key: "territories", label: "Territori", icon: Globe },
  { key: "categories", label: "Categorie", icon: Tag },
  { key: "media", label: "Libreria Media", icon: Image },
  { key: "bookings", label: "Prenotazioni", icon: Ticket },
  { key: "customers", label: "Clienti (CRM)", icon: Users },
  { key: "operators", label: "Operatori", icon: Users },
  { key: "settlement", label: "Rendiconti", icon: CreditCard },
  { key: "partners", label: "Partner Referral", icon: Building2 },
  { key: "sponsors", label: "Sponsor", icon: Megaphone },
  { key: "qr", label: "QR Generator", icon: QrCode },
  { key: "ranking", label: "Ranking Engine", icon: Sliders },
  { key: "payment", label: "Pagamenti", icon: CreditCard },
  { key: "content", label: "Contenuti sito", icon: LayoutDashboard },
  { key: "audit", label: "Log attività", icon: TrendingUp },
];

const CHART_COLORS = ["#004B87", "#00B4D8", "#E07A5F", "#f2c14e", "#2a9d8f", "#8d99ae"];

function Overview() {
  const statsQ = useQuery({ queryKey: ["adminStats"], queryFn: apiClient.adminStats });
  const dashQ = useQuery({ queryKey: ["adminDashboard", "30d"], queryFn: () => apiClient.adminDashboard("30d") });
  const s = statsQ.data;
  const d = dashQ.data;
  if (statsQ.isLoading) return <AdminLoading />;
  if (statsQ.isError) return <AdminError onRetry={() => { statsQ.refetch(); dashQ.refetch(); }} />;
  if (!s) return <AdminEmpty />;
  return (
    <div className="space-y-6">
      {d && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-1">Contabilità separata (30 giorni)</h3>
          <p className="text-xs text-slate-400 mb-4">Il GMV è il valore venduto, NON il ricavo di Cristalda.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] font-bold uppercase text-slate-400">GMV (venduto)</div><div className="text-lg font-bold text-slate-900">{eur(d.gross)}</div></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] font-bold uppercase text-slate-400">Dovuto operatori</div><div className="text-lg font-bold text-slate-900">{eur(d.operator_due)}</div></div>
            <div className="rounded-lg bg-primary/5 p-3"><div className="text-[11px] font-bold uppercase text-primary">Commissioni Cristalda</div><div className="text-lg font-bold text-primary">{eur(d.commission)}</div></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] font-bold uppercase text-slate-400">Commissioni referral</div><div className="text-lg font-bold text-slate-900">{eur(d.referral_commission)}</div></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] font-bold uppercase text-slate-400">Ricavi sponsor</div><div className="text-lg font-bold text-slate-900">{eur(d.sponsor_revenue)}</div></div>
            <div className="rounded-lg bg-red-50 p-3"><div className="text-[11px] font-bold uppercase text-red-400">Rimborsi</div><div className="text-lg font-bold text-red-500">{eur(d.refund_amount)}</div></div>
            <div className="rounded-lg bg-emerald-50 p-3 col-span-2"><div className="text-[11px] font-bold uppercase text-emerald-600">Margine netto Cristalda</div><div className="text-lg font-bold text-emerald-600">{eur(d.net_margin)}</div></div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Vendite oggi" value={eur(s.today_sales)} accent />
        <StatCard label="Commissioni oggi" value={eur(s.today_commissions)} accent />
        <StatCard label="Vendite mese" value={eur(s.month_sales)} />
        <StatCard label="Commissioni mese" value={eur(s.month_commissions)} />
        <StatCard label="GMV totale" value={eur(s.gmv)} sub="Valore servizi" />
        <StatCard label="Ricavi Cristalda" value={eur(s.revenue)} accent sub="Solo commissioni" />
        <StatCard label="Prenotazioni" value={s.bookings} />
        <StatCard label="Clienti" value={s.customers} />
        <StatCard label="Commissione media" value={eur(s.avg_commission)} />
        <StatCard label="Valore ordine medio" value={eur(s.avg_order_value)} />
        <StatCard label="Margine / cliente" value={eur(s.margin_per_customer)} />
        <StatCard label="Ricavi Sponsor" value={eur(s.sponsor_revenue)} />
        <StatCard label="Tasso cancellazione" value={`${s.cancellation_rate}%`} />
        <StatCard label="Tasso rimborso" value={`${s.refund_rate}%`} />
        <StatCard label="Vendite referral" value={eur(s.referral_sales)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Vendite & Commissioni (14 giorni)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={s.chart_days}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#004B87" stopOpacity={0.3} /><stop offset="100%" stopColor="#004B87" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00B4D8" stopOpacity={0.3} /><stop offset="100%" stopColor="#00B4D8" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="sales" stroke="#004B87" fill="url(#g1)" name="Vendite" />
              <Area type="monotone" dataKey="commissions" stroke="#00B4D8" fill="url(#g2)" name="Commissioni" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Vendite per categoria</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={s.top_categories}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>{s.top_categories.map((c, i) => <Cell key={c.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {[["Top Esperienze", s.top_experiences], ["Top Operatori", s.top_operators], ["Vendite per referral", s.referral_sales_by_partner]].map(([title, list]) => (
          <div key={title} className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">{title}</h3>
            <div className="space-y-2">
              {list.length === 0 && <p className="text-sm text-slate-400">Nessun dato.</p>}
              {list.map((r) => (
                <div key={`${title}-${r.name}`} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate pr-2">{r.name}</span>
                  <span className="font-bold text-primary whitespace-nowrap">{eur(r.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingsTab() {
  const qc = useQueryClient();
  const { data: bookings = [] } = useQuery({ queryKey: ["adminBookings"], queryFn: apiClient.adminBookings });
  const cancel = useMutation({ mutationFn: apiClient.cancelBooking, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminBookings"] }); toast.success("Prenotazione annullata, capacità ripristinata"); } });
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            {["Numero", "Cliente", "Esperienza", "Data", "GMV", "Commissione", "Stato", "Azioni"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-${b.booking_number}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-primary whitespace-nowrap">{b.booking_number}</td>
                <td className="px-4 py-3">{b.customer.name} {b.customer.surname}</td>
                <td className="px-4 py-3 max-w-[200px] truncate">{b.items[0]?.title}</td>
                <td className="px-4 py-3 whitespace-nowrap">{b.items[0]?.date}</td>
                <td className="px-4 py-3 font-semibold">{eur(b.gmv)}</td>
                <td className="px-4 py-3 text-primary font-semibold">{eur(b.cristalda_commission)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${b.status === "confirmed" ? "bg-emerald-50 text-emerald-600" : b.status === "cancelled" ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-500"}`}>{b.status}</span></td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <a href={`/voucher/${b.booking_number}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-secondary mr-3">Voucher</a>
                  {b.status !== "cancelled" && <button onClick={() => { if (window.confirm("Annullare questa prenotazione?")) cancel.mutate(b.booking_number); }} data-testid={`cancel-${b.booking_number}`} className="text-xs font-semibold text-red-500">Annulla</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OperatorDetail({ op, onClose }) {
  const { data: prof } = useQuery({ queryKey: ["adminOpProfile", op.id], queryFn: () => apiClient.adminGetOperatorProfile(op.id) });
  const { data: allExp = [] } = useQuery({ queryKey: ["adminExperiences"], queryFn: apiClient.adminExperiences });
  const { data: allProd = [] } = useQuery({ queryKey: ["adminProducts"], queryFn: apiClient.adminGetProducts });
  const o = prof?.operator || op;
  const completion = prof?.completion ?? null;
  const exps = allExp.filter((e) => e.operator_id === op.id);
  const prods = allProd.filter((p) => p.operator_id === op.id);
  const due = Math.max(0, (op.gmv || 0) - (op.commission_generated || 0));
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["adminOperators"] }); qc.invalidateQueries({ queryKey: ["adminOpProfile", op.id] }); };
  const act = (fn, msg) => async () => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const doApprove = act(() => apiClient.approveOperator(op.id), "Operatore approvato");
  const doReject = async () => { const n = window.prompt("Motivo del rifiuto (opzionale):", ""); if (n === null) return; try { await apiClient.rejectOperator(op.id, n); toast.success("Operatore rifiutato"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const doRequest = async () => { const n = window.prompt("Cosa deve correggere l'operatore?", ""); if (!n) return; try { await apiClient.requestOperatorChanges(op.id, n); toast.success("Modifiche richieste"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const doSuspend = act(() => apiClient.setOperatorStatus(op.id, "SUSPENDED"), "Operatore sospeso");
  const doReactivate = act(() => apiClient.setOperatorStatus(op.id, "ACTIVE"), "Operatore riattivato");
  const st = o.status;
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-50 text-sm">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span className="font-medium text-slate-800 text-right break-all">{value || "—"}</span>
    </div>
  );
  const Group = ({ title, children }) => (
    <div className="rounded-xl border border-slate-100 p-4">
      <div className="text-[11px] font-bold uppercase tracking-wide text-primary mb-2">{title}</div>
      {children}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="operator-detail-modal" onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-heading font-bold text-slate-900" data-testid="operator-detail-name">{o.name}</h2>
            <div className="mt-1 flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${o.status === "ACTIVE" ? "bg-emerald-50 text-emerald-600" : o.status === "SUSPENDED" ? "bg-red-50 text-red-500" : o.status === "INVITED" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{o.status || "—"}</span>
              <span className="text-xs text-slate-400">{o.primary_category}</span>
              {o.demo && <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[10px] font-bold">DEMO</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="operator-detail-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg bg-primary/5 p-3"><div className="text-[11px] font-bold uppercase text-primary">GMV</div><div className="text-lg font-bold text-primary" data-testid="operator-detail-gmv">{eur(op.gmv || 0)}</div></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] font-bold uppercase text-slate-400">Commissioni Cristalda</div><div className="text-lg font-bold text-slate-900">{eur(op.commission_generated || 0)}</div></div>
          <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[11px] font-bold uppercase text-emerald-600">Dovuto operatore</div><div className="text-lg font-bold text-emerald-700" data-testid="operator-detail-due">{eur(due)}</div></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] font-bold uppercase text-slate-400">Prenotazioni</div><div className="text-lg font-bold text-slate-900">{op.bookings_count || 0}</div></div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="operator-review-actions">
          {["PENDING", "PENDING_CORRECTION", "IN_VERIFICA", "REJECTED"].includes(st) && <button onClick={doApprove} data-testid="op-approve" className="rounded-full bg-emerald-500 text-white text-sm font-semibold px-4 py-2">Approva</button>}
          {["PENDING", "PENDING_CORRECTION", "IN_VERIFICA"].includes(st) && <button onClick={doRequest} data-testid="op-request-changes" className="rounded-full border border-amber-300 text-amber-600 text-sm font-semibold px-4 py-2">Richiedi modifiche</button>}
          {["PENDING", "PENDING_CORRECTION", "IN_VERIFICA"].includes(st) && <button onClick={doReject} data-testid="op-reject" className="rounded-full border border-red-300 text-red-500 text-sm font-semibold px-4 py-2">Rifiuta</button>}
          {st === "ACTIVE" && <button onClick={doSuspend} data-testid="op-suspend-modal" className="rounded-full border border-red-300 text-red-500 text-sm font-semibold px-4 py-2">Sospendi</button>}
          {st === "SUSPENDED" && <button onClick={doReactivate} data-testid="op-reactivate-modal" className="rounded-full bg-emerald-500 text-white text-sm font-semibold px-4 py-2">Riattiva</button>}
        </div>

        {completion !== null && (
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">Completamento profilo</span><span className="font-semibold text-slate-700" data-testid="operator-detail-completion">{completion}%</span></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${completion}%` }} /></div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <Group title="Attività">
            <Row label="Ragione sociale" value={o.legal_name} />
            <Row label="Commissione Cristalda" value={commissionLabel(o.commission_type, o.commission_value)} />
            <Row label="Vende prodotti" value={o.sells_products ? "Sì" : "No"} />
            <Row label="Offre esperienze" value={o.offers_experiences ? "Sì" : "No"} />
          </Group>
          <Group title="Referente">
            <Row label="Nome" value={[o.referent_name, o.referent_surname].filter(Boolean).join(" ")} />
            <Row label="Ruolo" value={o.referent_role} />
            <Row label="Telefono" value={o.referent_phone} />
            <Row label="Email" value={o.referent_email} />
          </Group>
          <Group title="Contatti">
            <Row label="Telefono" value={o.company_phone || o.phone} />
            <Row label="Mobile" value={o.mobile} />
            <Row label="WhatsApp" value={o.whatsapp} />
            <Row label="Email" value={o.email} />
          </Group>
          <Group title="Dati fiscali">
            <Row label="P.IVA" value={o.vat} />
            <Row label="Codice Fiscale" value={o.fiscal_code} />
            <Row label="PEC" value={o.pec} />
            <Row label="SDI" value={o.sdi} />
            <Row label="REA" value={o.rea} />
          </Group>
          <Group title="Indirizzo">
            <Row label="Sede legale" value={o.legal_address} />
            <Row label="Città" value={o.city} />
            <Row label="Provincia" value={o.province} />
            <Row label="Paese" value={o.country} />
          </Group>
          <Group title="Dati bancari (riservato)">
            <Row label="Intestatario" value={o.account_holder} />
            <Row label="IBAN" value={o.iban} />
            <Row label="BIC" value={o.bic} />
            <Row label="Banca" value={o.bank_name} />
          </Group>
        </div>

        <div className="mt-4 rounded-xl border border-slate-100 p-4">
          <div className="text-[11px] font-bold uppercase tracking-wide text-primary mb-2">Documenti ({(o.documents || []).length})</div>
          {(o.documents || []).length === 0 ? <p className="text-sm text-slate-400">Nessun documento caricato.</p> : (
            <div className="space-y-1.5">
              {o.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{d.name} <span className="text-slate-400">({d.doc_type})</span></span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${d.status === "VERIFICATO" ? "bg-emerald-50 text-emerald-600" : d.status === "RIFIUTATO" || d.status === "SCADUTO" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"}`}>{d.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-100 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-primary mb-2">Esperienze ({exps.length})</div>
            {exps.length === 0 ? <p className="text-sm text-slate-400">Nessuna esperienza.</p> : (
              <ul className="text-sm text-slate-700 space-y-1">{exps.map((e) => <li key={e.id}>• {e.title} <span className="text-slate-400">{eur(e.price)}</span></li>)}</ul>
            )}
          </div>
          <div className="rounded-xl border border-slate-100 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-primary mb-2">Prodotti ({prods.length})</div>
            {prods.length === 0 ? <p className="text-sm text-slate-400">Nessun prodotto.</p> : (
              <ul className="text-sm text-slate-700 space-y-1">{prods.map((p) => <li key={p.id}>• {p.name} <span className="text-slate-400">{eur(p.price)}</span></li>)}</ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OperatorsTab() {
  const qc = useQueryClient();
  const { data: operators = [] } = useQuery({ queryKey: ["adminOperators"], queryFn: apiClient.adminOperators });
  const [showNew, setShowNew] = useState(false);
  const [detailOp, setDetailOp] = useState(null);
  const [opFilter, setOpFilter] = useState("ALL");
  const [form, setForm] = useState({ name: "", email: "", primary_category: "barche-gommoni", commission_type: "percentage", commission_input: 12 });
  const commPct = form.commission_type === "percentage";
  const create = useMutation({ mutationFn: apiClient.createOperator, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminOperators"] }); setShowNew(false); toast.success("Operatore creato"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore nella creazione") });
  const doCreate = () => {
    if (!form.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    const raw = Number(form.commission_input);
    if (!raw || raw <= 0) { toast.error("Inserisci un valore di commissione valido"); return; }
    const commission_value = commPct ? pctToFrac(raw) : raw;
    if (commPct && (commission_value <= 0 || commission_value > 1)) { toast.error("La percentuale deve essere tra 0 e 100"); return; }
    create.mutate({ name: form.name, email: form.email, primary_category: form.primary_category, commission_type: form.commission_type, commission_value });
  };
  const update = useMutation({ mutationFn: ({ id, data }) => apiClient.updateOperator(id, data), onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminOperators"] }); toast.success("Operatore aggiornato"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore nell'aggiornamento operatore") });
  const invite = useMutation({ mutationFn: apiClient.inviteOperator, onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["adminOperators"] }); navigator.clipboard?.writeText(r.activation_url).catch(() => {}); toast.success("Invito creato — link copiato negli appunti"); window.prompt("Link di attivazione (copialo e invialo via WhatsApp/email):", r.activation_url); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  const setStatus = useMutation({ mutationFn: ({ id, status }) => apiClient.setOperatorStatus(id, status), onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["adminOperators"] }); toast.success(v.status === "SUSPENDED" ? "Operatore sospeso" : "Operatore riattivato"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore nel cambio stato operatore") });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <select data-testid="operator-status-filter" value={opFilter} onChange={(e) => setOpFilter(e.target.value)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">
          <option value="ALL">Tutti</option>
          <option value="PENDING">In attesa</option>
          <option value="PENDING_CORRECTION">Da correggere</option>
          <option value="ACTIVE">Attivi</option>
          <option value="SUSPENDED">Sospesi</option>
          <option value="REJECTED">Rifiutati</option>
          <option value="INVITED">Invitati</option>
        </select>
        {operators.some((o) => o.status === "PENDING") && <span data-testid="pending-badge" className="rounded-full bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5">{operators.filter((o) => o.status === "PENDING").length} nuovo/i operatore/i in attesa di approvazione</span>}
        <button onClick={() => setShowNew(!showNew)} data-testid="new-operator-btn" className="rounded-full bg-primary text-white text-sm font-semibold px-5 py-2">+ Nuovo operatore</button>
      </div>
      {showNew && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 grid sm:grid-cols-6 gap-3 items-end">
          <input placeholder="Nome" data-testid="op-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input placeholder="Email (per invito)" data-testid="op-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input placeholder="Categoria (slug)" value={form.primary_category} onChange={(e) => setForm({ ...form, primary_category: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <select value={form.commission_type} onChange={(e) => setForm({ ...form, commission_type: e.target.value })} data-testid="op-commission-type" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="percentage">Percentuale</option><option value="fixed_per_person">Fissa/persona</option><option value="fixed_booking">Fissa/prenotazione</option>
          </select>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">{commPct ? "Commissione Cristalda (%)" : "Commissione Cristalda (€)"}</label>
            <input type="number" min="0" step={commPct ? "1" : "0.5"} required placeholder={commPct ? "es. 15" : "es. 8"} data-testid="op-commission-value" value={form.commission_input} onChange={(e) => setForm({ ...form, commission_input: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <button onClick={doCreate} data-testid="op-save" className="rounded-lg bg-primary text-white text-sm font-semibold py-2">Salva</button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            {["Operatore", "Categoria", "Commissione", "Score", "GMV", "Commissioni gen.", "Prenotazioni", "Stato", "Account"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {operators.filter((o) => opFilter === "ALL" || (o.status || "INVITED") === opFilter).map((o) => (
              <tr key={o.id} data-testid={`admin-operator-${o.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold"><button data-testid={`op-detail-${o.id}`} onClick={() => setDetailOp(o)} className="text-primary hover:underline text-left">{o.name}</button></td>
                <td className="px-4 py-3 text-slate-500">{o.primary_category}</td>
                <td className="px-4 py-3">
                  <input type="number" step={o.commission_type === "percentage" ? "1" : "0.5"}
                    key={`${o.id}-${o.commission_value}`}
                    defaultValue={o.commission_type === "percentage" ? fracToPct(o.commission_value) : o.commission_value}
                    data-testid={`op-commission-${o.id}`}
                    onBlur={(e) => update.mutate({ id: o.id, data: { commission_value: o.commission_type === "percentage" ? pctToFrac(e.target.value) : Number(e.target.value) } })}
                    className="w-20 rounded border border-slate-200 px-2 py-1" />
                  <span className="text-xs text-slate-400 ml-1">{o.commission_type === "percentage" ? "%" : "€"}</span>
                  <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`op-commission-label-${o.id}`}>= {commissionLabel(o.commission_type, o.commission_value)}</div>
                </td>
                <td className="px-4 py-3"><span className="rounded-full bg-primary/5 text-primary font-bold px-2 py-0.5 text-xs">{o.ranking_score}</span></td>
                <td className="px-4 py-3">{eur(o.gmv)}</td>
                <td className="px-4 py-3 text-primary font-semibold">{eur(o.commission_generated)}</td>
                <td className="px-4 py-3">{o.bookings_count}</td>
                <td className="px-4 py-3">
                  <button onClick={() => update.mutate({ id: o.id, data: { active: !o.active } })} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.active ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>{o.active ? "Attivo" : "Disattivo"}</button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${o.status === "ACTIVE" ? "bg-emerald-50 text-emerald-600" : o.status === "SUSPENDED" ? "bg-red-50 text-red-500" : o.status === "INVITED" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{o.status || "—"}</span>
                    <div className="flex gap-1 flex-wrap">
                      <button data-testid={`op-invite-${o.id}`} onClick={() => invite.mutate(o.id)} className="text-[10px] font-semibold text-primary">Invita</button>
                      {o.status === "SUSPENDED"
                        ? <button data-testid={`op-reactivate-${o.id}`} onClick={() => setStatus.mutate({ id: o.id, status: "ACTIVE" })} className="text-[10px] font-semibold text-emerald-600">Riattiva</button>
                        : <button data-testid={`op-suspend-${o.id}`} onClick={() => setStatus.mutate({ id: o.id, status: "SUSPENDED" })} className="text-[10px] font-semibold text-red-500">Sospendi</button>}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detailOp && <OperatorDetail op={detailOp} onClose={() => setDetailOp(null)} />}
    </div>
  );
}

function PartnerDetail({ id, onClose }) {
  const qc = useQueryClient();
  const { data: p, isLoading, isError, refetch } = useQuery({ queryKey: ["partnerDetail", id], queryFn: () => apiClient.partnerDetail(id) });
  const [edit, setEdit] = useState(null);
  const save = useMutation({
    mutationFn: (data) => apiClient.updatePartner(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminPartners"] }); qc.invalidateQueries({ queryKey: ["partnerDetail", id] }); setEdit(null); toast.success("Partner aggiornato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="partner-detail" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-lg">Dettagli partner</h3>
          <button onClick={onClose} data-testid="partner-detail-close" className="text-slate-400 text-xl leading-none">×</button>
        </div>
        {isLoading ? <AdminLoading /> : isError || !p ? <AdminError onRetry={refetch} /> : (
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2 text-sm">
              {[["Nome", p.name], ["Tipo", p.type], ["Codice referral", p.code], ["Commissione", `${fracToPct(p.commission_pct)}%`],
                ["Click", p.clicks], ["Prenotazioni", p.bookings], ["GMV generato", eur(p.booking_value)],
                ["Conversione", `${p.conversion_rate}%`], ["Commissione partner", eur(p.partner_commission)],
                ["Stato", p.active ? "Attivo" : "Sospeso"], ["Creato", (p.created_at || "").slice(0, 10)]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-50 py-1"><span className="text-slate-400">{k}</span><span className="font-semibold text-slate-800">{v}</span></div>
              ))}
              <div className="pt-1">
                <div className="text-slate-400 text-xs mb-1">Referral URL</div>
                <div data-testid="partner-referral-url" className="font-mono text-xs break-all bg-slate-50 rounded p-2">{p.url}</div>
                <div className="mt-2 flex gap-2">
                  <button data-testid="partner-copy-link" onClick={() => copyLink(p.url)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold">Copia link</button>
                  <button data-testid="partner-edit-btn" onClick={() => setEdit({ name: p.name, type: p.type, commission_pct: p.commission_pct, active: p.active })} className="rounded-full border border-primary text-primary px-3 py-1.5 text-xs font-semibold">Modifica</button>
                </div>
              </div>
            </div>
            <div className="text-center">
              <img src={p.qr} alt={`QR ${p.name}`} data-testid="partner-qr-img" className="h-40 w-40 mx-auto border rounded-lg" />
              <button data-testid="partner-download-qr" onClick={() => downloadQR(p.qr, `qr-${p.code}`)} className="mt-3 rounded-full bg-primary text-white px-5 py-2 text-sm font-semibold">Scarica QR</button>
              <div className="mt-4 text-left">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">Prenotazioni attribuite</div>
                <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                  {(p.bookings_history || []).length === 0 ? <div className="text-slate-400">Nessuna prenotazione attribuita.</div> :
                    p.bookings_history.map((b) => <div key={b.booking_number} className="flex justify-between"><span>{b.booking_number}</span><span className="font-semibold">{eur(b.gmv)}</span></div>)}
                </div>
              </div>
            </div>
            {edit && (
              <div className="sm:col-span-2 border-t border-slate-100 pt-4 grid sm:grid-cols-4 gap-3">
                <input data-testid="edit-partner-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <select value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">{["hotel", "bnb", "camping", "residence", "restaurant"].map((v) => <option key={v} value={v}>{v}</option>)}</select>
                <input type="number" value={fracToPct(edit.commission_pct)} onChange={(e) => setEdit({ ...edit, commission_pct: pctToFrac(e.target.value) })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <select value={edit.active ? "1" : "0"} onChange={(e) => setEdit({ ...edit, active: e.target.value === "1" })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="1">Attivo</option><option value="0">Sospeso</option></select>
                <div className="sm:col-span-4 flex justify-end gap-2">
                  <button onClick={() => setEdit(null)} className="rounded-full border px-4 py-2 text-sm font-semibold">Annulla</button>
                  <button data-testid="save-partner-btn" onClick={() => save.mutate(edit)} className="rounded-full bg-primary text-white px-5 py-2 text-sm font-semibold">Salva</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PartnersTab() {
  const qc = useQueryClient();
  const { data: partners = [] } = useQuery({ queryKey: ["adminPartners"], queryFn: apiClient.adminPartners });
  const [form, setForm] = useState({ name: "", type: "hotel", commission_pct: 0.05 });
  const [openId, setOpenId] = useState(null);
  const create = useMutation({ mutationFn: apiClient.createPartner, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminPartners"] }); setForm({ name: "", type: "hotel", commission_pct: 0.05 }); toast.success("Partner creato"); } });
  return (
    <div>
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 grid sm:grid-cols-4 gap-3">
        <input placeholder="Nome partner" data-testid="partner-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {["hotel", "bnb", "camping", "residence", "restaurant"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <input type="number" step="1" placeholder="Commissione %" value={fracToPct(form.commission_pct)} onChange={(e) => setForm({ ...form, commission_pct: pctToFrac(e.target.value) })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button onClick={() => create.mutate(form)} data-testid="create-partner-btn" className="rounded-lg bg-primary text-white text-sm font-semibold">+ Crea partner</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map((p) => (
          <button key={p.id} data-testid={`partner-card-${p.id}`} onClick={() => setOpenId(p.id)} className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-primary hover:shadow-sm transition-all">
            <div className="flex items-center justify-between mb-2"><h3 className="font-heading font-semibold text-slate-900">{p.name}</h3><span className="text-xs rounded-full bg-slate-100 px-2 py-0.5">{p.type}</span></div>
            <div className="text-xs text-secondary font-mono mb-3">/r/{p.code}</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><div className="text-slate-400 text-xs">Click</div><div className="font-semibold">{p.clicks}</div></div>
              <div><div className="text-slate-400 text-xs">Prenotazioni</div><div className="font-semibold">{p.bookings}</div></div>
              <div><div className="text-slate-400 text-xs">Valore</div><div className="font-semibold">{eur(p.booking_value)}</div></div>
              <div><div className="text-slate-400 text-xs">Conversione</div><div className="font-semibold">{p.conversion_rate}%</div></div>
              <div><div className="text-slate-400 text-xs">Commissione %</div><div className="font-semibold">{fracToPct(p.commission_pct)}%</div></div>
              <div><div className="text-slate-400 text-xs">Commissione partner</div><div className="font-semibold text-primary">{eur(p.partner_commission)}</div></div>
            </div>
            <div className="mt-3 text-xs font-semibold text-primary">Apri dettagli →</div>
          </button>
        ))}
      </div>
      {openId && <PartnerDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

const SP_PLACEMENTS = [
  ["HOME", "Home"], ["ALLOGGI", "Alloggi"], ["ESPERIENZE", "Esperienze"],
  ["RISTORANTI", "Ristoranti"], ["SHOP", "Shop"], ["DESTINAZIONE", "Destinazione"],
];
const SP_STATUS_STYLE = {
  ATTIVO: "bg-emerald-50 text-emerald-600", SCADUTO: "bg-red-50 text-red-500",
  PROGRAMMATO: "bg-amber-50 text-amber-600", INACTIVE: "bg-slate-100 text-slate-500",
};
const emptySponsor = { company: "", image_url: "", link_url: "", placements: ["HOME"], start_date: "", end_date: "", priority: 0, active: true, level: "LOCAL_PARTNER", contract_value: 0, notes: "" };

function SponsorEditor({ sponsor, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState(sponsor ? {
    company: sponsor.company || "", image_url: sponsor.image_url || "", link_url: sponsor.link_url || "",
    placements: sponsor.placements?.length ? sponsor.placements : ["HOME"], start_date: sponsor.start_date || "",
    end_date: sponsor.end_date || "", priority: sponsor.priority || 0, active: sponsor.active !== false,
    level: sponsor.level || "LOCAL_PARTNER", contract_value: sponsor.contract_value || 0, notes: sponsor.notes || "",
  } : { ...emptySponsor });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const togglePlacement = (p) => set("placements", f.placements.includes(p) ? f.placements.filter((x) => x !== p) : [...f.placements, p]);
  const save = useMutation({
    mutationFn: () => (sponsor ? apiClient.updateSponsor(sponsor.id, f) : apiClient.createSponsor(f)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminSponsors"] }); toast.success(sponsor ? "Sponsor aggiornato" : "Sponsor creato"); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const doUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const { url } = await apiClient.uploadMedia(file); set("image_url", url); toast.success("Immagine caricata"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload fallito"); }
    finally { setUploading(false); }
  };
  const submit = () => {
    if (!f.company.trim()) return toast.error("Inserisci il nome dell'azienda");
    if (!f.placements.length) return toast.error("Seleziona almeno un posizionamento");
    if (f.start_date && f.end_date && f.end_date < f.start_date) return toast.error("La data di fine deve essere dopo l'inizio");
    save.mutate();
  };
  const sinp = "rounded-lg border border-slate-200 px-3 py-2 text-sm w-full outline-none focus:ring-2 focus:ring-secondary";
  const slbl = "text-xs font-bold uppercase tracking-wide text-slate-400";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="sponsor-editor">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-lg">{sponsor ? "Modifica sponsor" : "Nuovo sponsor"}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="sm:col-span-2"><span className={slbl}>Azienda</span><input data-testid="sponsor-company" className={sinp} value={f.company} onChange={(e) => set("company", e.target.value)} /></label>
          <label className="sm:col-span-2"><span className={slbl}>Link (opzionale)</span><input data-testid="sponsor-link" className={sinp} placeholder="https://..." value={f.link_url} onChange={(e) => set("link_url", e.target.value)} /></label>
          <label><span className={slbl}>Data inizio</span><input type="date" data-testid="sponsor-start" className={sinp} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></label>
          <label><span className={slbl}>Data fine (scadenza)</span><input type="date" data-testid="sponsor-end" className={sinp} value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></label>
          <label><span className={slbl}>Priorità</span><input type="number" data-testid="sponsor-priority" className={sinp} value={f.priority} onChange={(e) => set("priority", Number(e.target.value))} /></label>
          <label><span className={slbl}>Valore contratto €</span><input type="number" className={sinp} value={f.contract_value} onChange={(e) => set("contract_value", Number(e.target.value))} /></label>
        </div>
        <div className="mt-4">
          <span className={slbl}>Posizionamenti</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {SP_PLACEMENTS.map(([key, label]) => (
              <button key={key} type="button" data-testid={`sponsor-placement-${key}`} onClick={() => togglePlacement(key)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${f.placements.includes(key) ? "bg-primary text-white border-primary" : "bg-white text-slate-500 border-slate-200"}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <span className={slbl}>Immagine (PNG trasparente / JPG / WebP)</span>
          <div className="flex items-center gap-3 mt-2">
            {f.image_url ? (
              <div className="relative h-24 w-40 rounded-lg overflow-hidden border bg-[repeating-conic-gradient(#f1f5f9_0_25%,#fff_0_50%)] bg-[length:16px_16px] flex items-center justify-center">
                <img src={f.image_url} alt="" className="max-h-full max-w-full object-contain" />
                <button onClick={() => set("image_url", "")} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ) : null}
            <button onClick={() => fileRef.current.click()} data-testid="sponsor-upload-btn" className="h-24 w-40 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 text-xs">
              {uploading ? "..." : <><Upload className="h-4 w-4 mb-1" /> Carica logo</>}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={doUpload} />
          </div>
          <input className={sinp + " mt-2"} placeholder="oppure incolla URL immagine" value={f.image_url} onChange={(e) => set("image_url", e.target.value)} />
        </div>
        <label className="flex items-center gap-2 mt-4"><input type="checkbox" data-testid="sponsor-active" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Attivo</label>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="rounded-full border px-5 py-2 text-sm font-semibold">Annulla</button>
          <button onClick={submit} disabled={save.isPending} data-testid="sponsor-save" className="rounded-full bg-primary text-white px-6 py-2 text-sm font-semibold disabled:opacity-50">{save.isPending ? "Salvataggio..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

function SponsorsTab() {
  const qc = useQueryClient();
  const { data: sponsors = [], isLoading, isError } = useQuery({ queryKey: ["adminSponsors"], queryFn: apiClient.adminSponsors });
  const [editing, setEditing] = useState(null); // sponsor object or "new"
  const del = useMutation({ mutationFn: apiClient.deleteSponsor, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminSponsors"] }); toast.success("Sponsor eliminato"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  const toggle = useMutation({ mutationFn: apiClient.toggleSponsor, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminSponsors"] }); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  if (isLoading) return <AdminLoading />;
  if (isError) return <AdminError onRetry={() => qc.invalidateQueries({ queryKey: ["adminSponsors"] })} />;
  return (
    <div data-testid="sponsors-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Gestisci le sponsorizzazioni: immagine, posizionamento, date e priorità. Gli sponsor scaduti restano archiviati come <span className="font-semibold text-red-500">SCADUTO</span>.</p>
        <button onClick={() => setEditing("new")} data-testid="sponsor-new-btn" className="shrink-0 rounded-full bg-primary text-white px-4 py-2 text-sm font-semibold">+ Nuovo sponsor</button>
      </div>
      {sponsors.length === 0 ? <AdminEmpty message="Nessuno sponsor. Crea il primo." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sponsors.map((s) => (
            <div key={s.id} data-testid={`sponsor-card-${s.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="h-24 rounded-lg mb-3 bg-[repeating-conic-gradient(#f1f5f9_0_25%,#fff_0_50%)] bg-[length:16px_16px] flex items-center justify-center overflow-hidden">
                {s.image_url ? <img src={s.image_url} alt={s.company} className="max-h-full max-w-full object-contain" /> : <span className="text-slate-300 text-xs">Nessuna immagine</span>}
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900">{s.company}</h3>
                <span data-testid={`sponsor-status-${s.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SP_STATUS_STYLE[s.status] || "bg-slate-100 text-slate-500"}`}>{s.status}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(s.placements || []).map((p) => <span key={p} className="rounded bg-slate-50 text-slate-500 px-1.5 py-0.5 text-[10px] font-semibold">{p}</span>)}
              </div>
              <div className="text-xs text-slate-400 mt-2">
                {s.start_date || "—"} → {s.end_date || "∞"} · priorità {s.priority || 0}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(s)} data-testid={`sponsor-edit-${s.id}`} className="flex-1 rounded-lg border border-slate-200 py-1.5 text-xs font-semibold flex items-center justify-center gap-1"><Pencil className="h-3 w-3" /> Modifica</button>
                <button onClick={() => toggle.mutate(s.id)} data-testid={`sponsor-toggle-${s.id}`} title={s.active ? "Disattiva" : "Attiva"} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-slate-500"><Power className="h-3.5 w-3.5" /></button>
                <button onClick={() => { if (window.confirm(`Eliminare lo sponsor "${s.company}"?`)) del.mutate(s.id); }} data-testid={`sponsor-delete-${s.id}`} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <SponsorEditor sponsor={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function QRTab() {
  const qc = useQueryClient();
  const { data: sources = [] } = useQuery({ queryKey: ["adminQR"], queryFn: apiClient.adminQR });
  const [form, setForm] = useState({ label: "", kind: "poster" });
  const create = useMutation({ mutationFn: apiClient.createQR, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminQR"] }); toast.success("QR creato"); } });
  return (
    <div>
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 grid sm:grid-cols-3 gap-3">
        <input placeholder="Etichetta (es. Poster spiaggia)" data-testid="qr-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">{["poster", "hotel", "bnb", "restaurant", "operator", "beach", "shop", "social"].map((v) => <option key={v}>{v}</option>)}</select>
        <button onClick={() => create.mutate(form)} data-testid="create-qr-btn" className="rounded-lg bg-primary text-white text-sm font-semibold">+ Genera QR</button>
      </div>
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {sources.map((s) => (
          <div key={s.id} data-testid={`qr-source-${s.id}`} className="bg-white rounded-xl border border-slate-200 p-5 text-center">
            <img src={s.qr} alt={s.label} className="h-28 w-28 mx-auto" />
            <div className="font-semibold text-slate-800 mt-2 text-sm">{s.label}</div>
            <div className="text-xs text-slate-400">{s.kind}</div>
            <div className="mt-2 flex justify-center gap-4 text-xs"><span>Scan: <b>{s.scans}</b></span><span>Prenot.: <b>{s.bookings}</b></span></div>
            <div className="mt-3 flex justify-center gap-2">
              <button data-testid={`qr-copy-${s.id}`} onClick={() => copyLink(`${window.location.origin}/?src=${s.code}`)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold">Copia link</button>
              <button data-testid={`qr-download-${s.id}`} onClick={() => downloadQR(s.qr, `qr-${s.code}`)} className="rounded-full bg-primary text-white px-3 py-1.5 text-xs font-semibold">Scarica QR</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RankingTab() {
  const qc = useQueryClient();
  const { data: weights } = useQuery({ queryKey: ["weights"], queryFn: apiClient.getWeights });
  const [local, setLocal] = useState(null);
  const w = local || weights;
  const save = useMutation({ mutationFn: apiClient.setWeights, onSuccess: () => { qc.invalidateQueries(); toast.success("Pesi aggiornati e ranking ricalcolato"); } });
  if (!w) return <div className="text-slate-400">Caricamento...</div>;
  const labels = { quality: "Qualità/Rating cliente", availability: "Disponibilità reale", margin: "Margine Cristalda", value: "Valore/Prezzo cliente", reliability: "Affidabilità", confirmation: "Velocità conferma", refund: "Gestione rimborsi" };
  const total = Object.values(w).reduce((a, b) => a + b, 0);
  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-slate-900">Motore di Ranking Operatori</h3></div>
        <p className="text-sm text-slate-500 mb-5">Regola i pesi. Il posizionamento sponsorizzato resta sempre etichettato "Sponsorizzato" e non altera il ranking organico.</p>
        {Object.keys(labels).map((k) => (
          <div key={k} className="mb-4">
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{labels[k]}</span><span className="font-bold text-primary">{Math.round((w[k] || 0) * 100)}%</span></div>
            <input type="range" min="0" max="0.5" step="0.05" value={w[k]} data-testid={`weight-${k}`} onChange={(e) => setLocal({ ...w, [k]: Number(e.target.value) })} className="w-full accent-primary" />
          </div>
        ))}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <span className="text-sm text-slate-500">Totale: <b className={Math.abs(total - 1) < 0.001 ? "text-emerald-600" : "text-red-500"}>{Math.round(total * 100)}%</b>{Math.abs(total - 1) >= 0.001 && <span className="text-red-500 text-xs ml-2">Deve essere 100%</span>}</span>
          <button onClick={() => save.mutate(w)} disabled={Math.abs(total - 1) >= 0.001} data-testid="save-weights-btn" className="rounded-full bg-primary text-white text-sm font-semibold px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed">Salva e ricalcola</button>
        </div>
      </div>
    </div>
  );
}

function PaymentTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["paymentMode"], queryFn: apiClient.getPaymentMode });
  const save = useMutation({ mutationFn: apiClient.setPaymentMode, onSuccess: () => { qc.invalidateQueries({ queryKey: ["paymentMode"] }); toast.success("Modalità pagamento aggiornata"); } });
  const modes = [
    { key: "MODE_A", title: "Mode A — Pagamento diretto operatore", desc: "Il cliente paga l'operatore. Cristalda registra la commissione." },
    { key: "MODE_B", title: "Mode B — Pagamento a Cristalda", desc: "Il cliente paga Cristalda, che poi salda l'operatore." },
    { key: "MODE_C", title: "Mode C — Split payment marketplace", desc: "Il provider divide automaticamente la quota operatore e la commissione." },
  ];
  return (
    <div className="max-w-2xl space-y-3">
      <div className="rounded-xl bg-amber-50 text-amber-700 text-sm px-4 py-3">Pagamenti in modalità MOCK/TEST. Architettura modulare pronta per l'integrazione reale.</div>
      {modes.map((m) => (
        <button key={m.key} onClick={() => save.mutate(m.key)} data-testid={`payment-mode-${m.key}`} className={`w-full text-left rounded-xl border-2 p-5 transition-colors ${data?.mode === m.key ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <div className="font-heading font-semibold text-slate-900">{m.title}</div>
          <div className="text-sm text-slate-500 mt-1">{m.desc}</div>
        </button>
      ))}
    </div>
  );
}

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "overview";
  const tab = TABS.some((t) => t.key === rawTab) ? rawTab : "overview";
  const setTab = (key) => setSearchParams(key === "overview" ? {} : { tab: key });
  const titles = Object.fromEntries(TABS.map((t) => [t.key, t.label]));
  return (
    <DashboardShell title={`Gestione · ${titles[tab]}`} tabs={TABS} active={tab} onTab={setTab}>
      {tab === "overview" && <Overview />}
      {tab === "experiences" && <ExperiencesTab />}
      {tab === "activities" && <ActivitiesAdminTab />}
      {tab === "products" && <ProductsAdminTab />}
      {tab === "accommodations" && <AccommodationsAdminTab />}
      {tab === "restaurants" && <RestaurantsAdminTab />}
      {tab === "territories" && <TerritoriesAdminTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "media" && <MediaTab />}
      {tab === "bookings" && <BookingsTab />}
      {tab === "customers" && <CustomersTab />}
      {tab === "operators" && <OperatorsTab />}
      {tab === "settlement" && <SettlementTab />}
      {tab === "partners" && <PartnersTab />}
      {tab === "sponsors" && <SponsorsTab />}
      {tab === "qr" && <QRTab />}
      {tab === "ranking" && <RankingTab />}
      {tab === "payment" && <PaymentTab />}
      {tab === "content" && <ContentTab />}
      {tab === "audit" && <AuditTab />}
    </DashboardShell>
  );
}
